import { type FC, useCallback, useEffect, useState } from "react";

import { Button, Control, Spinner, TextArea } from "@snapchat/snap-design-system";

import { Flexbox } from "../../../shared/components";
import { useLensChangeTracker } from "../../hooks/useLensChangeTracker";
import { LENS_DESCRIPTION_MAX_CHARS } from "../constants";
import { FieldLabel } from "../shared";

import { container } from "./LensDescription.module.css";
import {
    LensDescriptionCancel,
    LensDescriptionEdit,
    LensDescriptionLabel,
    LensDescriptionSave,
} from "./messages.generated";

interface LensDescriptionProps {
    lensId: string;
    disabled?: boolean;
}

export const LensDescription: FC<LensDescriptionProps> = ({ disabled, lensId }) => {
    const [isEditing, setIsEditing] = useState(false);
    const { description, updateDescription, hasFieldChanged, updateLensLoading } = useLensChangeTracker(lensId);

    const [newDescription, setNewDescription] = useState(description);

    useEffect(() => {
        setNewDescription(description);
    }, [description]);

    const handleEditToggle = useCallback(() => {
        if (isEditing) {
            updateDescription(newDescription ?? "");
        } else {
            setNewDescription(description ?? "");
        }
        setIsEditing(!isEditing);
    }, [isEditing, newDescription, updateDescription, description]);

    const handleCancel = useCallback(() => {
        setNewDescription(description);
        setIsEditing(false);
    }, [description]);

    return (
        <Control
            label={
                <FieldLabel hasChanges={hasFieldChanged("description")}>
                    <LensDescriptionLabel />
                </FieldLabel>
            }
            className={container}
        >
            <Flexbox gap="8px" flexDirection="column">
                <TextArea
                    readOnly={!isEditing}
                    disabled={updateLensLoading}
                    value={newDescription ?? ""}
                    onChange={setNewDescription}
                    maxLength={LENS_DESCRIPTION_MAX_CHARS}
                    autoSize={{ minRows: 3 }}
                />
                <Flexbox gap="8px">
                    <Button size="small" isDisabled={disabled} onClick={handleEditToggle}>
                        {updateLensLoading ? (
                            <Spinner />
                        ) : isEditing ? (
                            <LensDescriptionSave />
                        ) : (
                            <LensDescriptionEdit />
                        )}
                    </Button>
                    {isEditing && (
                        <Button size="small" onClick={handleCancel}>
                            <LensDescriptionCancel />
                        </Button>
                    )}
                </Flexbox>
            </Flexbox>
        </Control>
    );
};
